import { useMemo } from 'react'

import { create } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { listDemandProcurementMasters } from '@ebalance/proto/bffuser/v1/occto_master_service-OcctoMasterService_connectquery'
import { ListDemandProcurementMastersRequestSchema } from '@ebalance/proto/bffuser/v1/occto_master_service_pb'

import { protoDateToCivilDate } from '~/models/civilDate'
import type {
  BgValidityPeriod,
  DemandProcurementBgItem,
} from '~/models/planSubmission/isDemandProcurementBgActiveOn'
import { useListBgMasters } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { useGetPersonalSettingsQuery } from '~/pages/Settings/hooks/useGetPersonalSettingsQuery'

const NO_AREAS: AreaCode[] = []

export type CreatePlanDialogCandidates = {
  bgMasterItems: DemandProcurementBgItem[]
  bgMasterPeriods: ReadonlyMap<string, BgValidityPeriod>
  bgNames: ReadonlyMap<string, string>
  businessAreas: AreaCode[]
  isCandidateDataReady: boolean
}

/**
 * 計画作成ダイアログ (`CreatePlanDialog`) の入力候補: 需要調達計画マスタ全件 +
 * BG マスタ本体 (有効期間・名称) + 組織の業務エリア設定。一覧の「計画作成」 と、
 * 詳細の「計画を作り直す」 (AC-PLC-020-2) が同じ候補でダイアログを開く。
 *
 * `isCandidateDataReady` は 3 つの取得がすべて成功で確定した時だけ true。読み込み中・
 * 取得失敗・オフラインでの paused (isLoading / isError が両方 false のまま data 未取得)
 * では空配列フォールバックが確定 0 件と区別できず、エリア候補 0 件の案内 (AC-PLC-061) が
 * 個人設定修正へ誤誘導するため、isSuccess で確定を判定する。
 *
 * @param options.enabled false の間は取得しない (未指定なら常に取得する)。
 * @param options.throwOnError 未指定なら QueryClient 既定 (= error boundary へ飛ばす) に従う。
 */
export function useCreatePlanDialogCandidates(options?: {
  enabled?: boolean
  throwOnError?: boolean
}): CreatePlanDialogCandidates {
  // 未指定の key は渡さない (`undefined` を渡すと QueryClient 既定を上書きする)。
  const queryOptions = {
    ...(options?.enabled !== undefined ? { enabled: options.enabled } : {}),
    ...(options?.throwOnError !== undefined
      ? { throwOnError: options.throwOnError }
      : {}),
  }
  const { data: masterData, isSuccess: isMasterSuccess } = useQuery(
    listDemandProcurementMasters,
    create(ListDemandProcurementMastersRequestSchema),
    queryOptions,
  )
  const { data: bgMasterListData, isSuccess: isBgMasterSuccess } =
    useListBgMasters(queryOptions)
  const { data: personalSettings, isSuccess: isPersonalSettingsSuccess } =
    useGetPersonalSettingsQuery(queryOptions)

  const bgMasterItems: DemandProcurementBgItem[] = useMemo(
    () =>
      (masterData?.items ?? []).flatMap((item) => {
        const validFrom = protoDateToCivilDate(item.validFrom)
        if (validFrom === undefined) return []
        return [
          {
            bgCode: item.bgCode,
            validFrom,
            validTo: protoDateToCivilDate(item.validTo),
          },
        ]
      }),
    [masterData],
  )
  const bgMasterPeriods: ReadonlyMap<string, BgValidityPeriod> = useMemo(
    () =>
      new Map(
        (bgMasterListData?.items ?? []).flatMap((item) => {
          const validFrom = protoDateToCivilDate(item.validFrom)
          if (validFrom === undefined) return []
          return [
            [
              item.bgCode,
              {
                validFrom,
                validTo: protoDateToCivilDate(item.validTo),
              },
            ] as const,
          ]
        }),
      ),
    [bgMasterListData],
  )
  const bgNames: ReadonlyMap<string, string> = useMemo(
    () =>
      new Map(
        (bgMasterListData?.items ?? []).map((item) => [
          item.bgCode,
          item.bgName,
        ]),
      ),
    [bgMasterListData],
  )

  return {
    bgMasterItems,
    bgMasterPeriods,
    bgNames,
    businessAreas: personalSettings?.settings?.businessAreas ?? NO_AREAS,
    isCandidateDataReady:
      isMasterSuccess && isBgMasterSuccess && isPersonalSettingsSuccess,
  }
}
